'use client'

import { useIsAuthenticated } from '@/lib/auth/client/AuthProvider.client'
import { twMerge } from '@/lib/classname/twMerge'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { Unit } from '@gql-generated/graphql'
import type React from 'react'
import type { ProductPriceProps } from '../product-price/ProductPrice.client'
import { ProductTiersPricesGrid } from './ProductTiersPricesGrid.client'

export type ProductTierPriceProps = {
  quantity?: number | null | undefined
  exclTax?: number | null | undefined
}

export type ProductTiersPricesProps = StrictPick<ProductPriceProps, 'packaging'> & {
  /** The price tiers */
  tiersPrices: Array<ProductTierPriceProps>

  priceQuantity?: number | null

  /** Additional classes to add to the component */
  className?: string
}

export function ProductTiersPrices({
  packaging,
  priceQuantity,
  tiersPrices,
  className,
}: ProductTiersPricesProps): React.ReactElement | null {
  const isAuthenticated = useIsAuthenticated()
  const { t } = useTranslation(['productTiersPrices'])
  const quantity = priceQuantity ?? packaging?.quantity ?? 1
  const unit = packaging?.unit ?? Unit.P

  if (!isAuthenticated || !tiersPrices?.length) {
    return null
  }

  // Filter valid tiers
  const validTiers = tiersPrices.filter(
    (tier): tier is { quantity: number; exclTax: number } =>
      typeof tier.quantity === 'number' && typeof tier.exclTax === 'number' && tier.exclTax > 0,
  )

  if (validTiers.length === 0) {
    return null
  }

  return (
    <div
      className={twMerge('flex w-full flex-col gap-1', className)}
      data-testid="product-tiers-price"
    >
      <span>
        <strong>{t('productTiersPrices:pricePerQuantity')}</strong>
      </span>

      <ProductTiersPricesGrid tiersPrices={tiersPrices} quantity={quantity} unit={unit} />
    </div>
  )
}
